/**
 * 平台差異（desktop-app.md 第 9 節）。
 *
 * 採**執行期偵測 + 動態 import**，網頁版與桌面版共用同一份 `dist/`。
 * 不做雙 build target：差異只有五個點，維護兩套 build 與兩輪測試的成本
 * 高於它省下的幾 KB，而且容易產生「只在一邊出現」的 bug。
 *
 * 這個檔案本身**不得 import 任何 Tauri 套件**，否則網頁版的 initial bundle
 * 會把它們一起帶進去。所有 Tauri API 一律在呼叫點用 `await import()` 載入。
 */

export type PlatformKind = 'desktop' | 'browser';

/**
 * Tauri 2 在 WebView 啟動時就注入 `__TAURI_INTERNALS__`，比 `window.__TAURI__`
 * 可靠：後者要在設定裡開 `withGlobalTauri` 才會有。
 */
export function isDesktop(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

export function platformKind(): PlatformKind {
  return isDesktop() ? 'desktop' : 'browser';
}

export interface PlatformIdentity {
  kind: PlatformKind;
  /** 工具列上的短標籤。 */
  label: string;
  /** 標籤右邊那句「資料放在哪」。 */
  location: string;
  /** tooltip 用的完整說明。 */
  detail: string;
}

/**
 * 兩邊的工具列都必須明確標示自己是誰（4.2）。
 *
 * 這不是裝飾：兩個版本的資料在瀏覽器安全模型下**必然分家**，
 * 同時開著不會壞也不會互相汙染，但也永遠不會同步。使用者要能一眼分辨
 * 現在打的字會存到哪一邊，否則會以為資料不見了。
 */
export function describePlatform(host?: string): PlatformIdentity {
  if (isDesktop()) {
    return {
      kind: 'desktop',
      label: '本機版',
      location: '文件\\GentleTask',
      detail:
        '這是安裝在這台電腦上的本機版。資料存在這台電腦，' +
        '筆記圖片保存在「文件\\GentleTask\\images\\notes」，請到「備份與還原」匯出完整 ZIP，將資料與圖片一起帶走。' +
        '本機版與瀏覽器版的資料是各自獨立的兩份，不會互相同步。',
    };
  }

  const hostname =
    host ?? (typeof window === 'undefined' ? 'gentle-task.pages.dev' : window.location.hostname);

  return {
    kind: 'browser',
    label: '瀏覽器版',
    location: hostname,
    detail:
      `這是在 ${hostname} 上執行的瀏覽器版。資料只保存在這個瀏覽器裡，` +
      '換一台電腦或換一個瀏覽器就看不到，請定期到「備份與還原」匯出。' +
      '本機版與瀏覽器版的資料是各自獨立的兩份，不會互相同步。',
  };
}

/**
 * 「你的資料放在哪」的一句話。
 *
 * 這句話散在助理區、空白狀態、備份頁三個地方，桌面版沿用瀏覽器版的說法會直接說謊：
 * 本機版的資料與 PNG 分開保存，完整 ZIP 才會一起帶走。
 * 集中成一個函式，之後再增加平台時不會有地方漏掉。
 */
export function storageScopeSentence(): string {
  return isDesktop()
    ? '資料與筆記圖片存在這台電腦，請定期匯出完整 ZIP 備份。'
    : '資料只保存在這個瀏覽器裡。';
}
